import React, { useContext, useEffect, useMemo } from "react";
import { PFieldsetWrapper } from "@porsche-design-system/components-react";
import { spacingFluidSmall } from "@porsche-design-system/components-js/styles";
import { useTranslation } from "react-i18next";
import CButton from "Components/CComponents/CButton/CButton";
import CText from "Components/CComponents/CText/CText";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";

/**
 * Card visual para un modelo de tuk-tuk.
 */
const TuktukCard = ({ label, capacity, units = 0 }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.25rem",
      padding: "1rem",
      borderRadius: "0.75rem",
      boxShadow: "0 2px 8px rgba(0,0,0,.08)",
      minWidth: "120px",
      minHeight: "120px",
      position: "relative",
    }}
  >
    {units > 0 && (
      <span
        style={{
          position: "absolute",
          top: "0.5rem",
          right: "0.5rem",
          background: "#d91b1b",
          color: "#fff",
          borderRadius: "999px",
          padding: "0.15rem 0.55rem",
          fontSize: "0.75rem",
          fontWeight: "600",
          lineHeight: 1,
        }}
      >
        {units}
      </span>
    )}

    <span role="img" aria-label="tuktuk" style={{ fontSize: "2rem" }}>
      🛺
    </span>
    <strong>{label}</strong>
    <span style={{ fontSize: "0.875rem", opacity: 0.8 }}>
      {capacity} plazas
    </span>
  </div>
);

const AutoTuktukModelsSection = ({
  persons,
  tuktukModels,
  formData,
  setFormData,
  userData,
  onSelectionDone,
  textOrigin,
  textBranch,
  isMinVehicleSmall,
}) => {
  const { isTuk } = useContext(AuthContext);

  //=========== Selección automática ===========//
  useEffect(() => {
    if (
      !persons ||
      persons <= 0 ||
      !Array.isArray(tuktukModels) ||
      tuktukModels.length === 0
    ) {
      return;
    }

    const selection = isMinVehicleSmall
      ? computeMinVehicleSmallSelection(tuktukModels, persons)
      : computeBestCombination(tuktukModels, persons);

    if (selection) {
      const totalCapacity = selection.reduce(
        (acc, s) => acc + s.units * (s.model.maxPeople || 0),
        0
      );
      const totalUnits = selection.reduce((acc, s) => acc + s.units, 0);

      setFormData((prev) => ({
        ...prev,
        tuktukModels: selection.map(({ model, units }) => ({
          value: model.value,
          units,
        })),
        totalCapacity,
        totalUnits,
      }));

      onSelectionDone?.(selection);
    } else {
      // No hay combinación válida (modo small) => limpiamos y forzamos contacto
      setFormData((prev) => ({
        ...prev,
        tuktukModels: [],
        totalCapacity: 0,
        totalUnits: 0,
      }));
      onSelectionDone?.(null);
    }
  }, [persons, tuktukModels, setFormData, onSelectionDone, isMinVehicleSmall]);

  //=========== Derivados desde formData ===========//

  const selectedModels = useMemo(
    () =>
      (formData?.tuktukModels || [])
        .map((sel) => {
          const model = tuktukModels.find((m) => m.value === sel.value);
          return model
            ? {
                value: sel.value,
                units: sel.units,
                model,
              }
            : null;
        })
        .filter(Boolean),
    [formData?.tuktukModels, tuktukModels]
  );

  const totalUnits = useMemo(
    () => selectedModels.reduce((sum, { units }) => sum + units, 0),
    [selectedModels]
  );

  const realCapacity = useMemo(
    () =>
      selectedModels.reduce((acc, { units, model }) => {
        const cap = Number.isFinite(model.maxPeople) ? model.maxPeople : 0;
        return acc + cap * units;
      }, 0),
    [selectedModels]
  );

  // Modelo más pequeño entre TODOS los modelos
  const minModel = useMemo(() => {
    const valid = (tuktukModels || []).filter(
      (m) => Number.isFinite(m.maxPeople) && m.maxPeople > 0
    );
    if (!valid.length) return null;
    return valid.reduce((min, m) =>
      m.maxPeople < min.maxPeople ? m : min
    );
  }, [tuktukModels]);

  const minCap = minModel?.maxPeople || 0;

  // Capacidad mostrada al cliente
  const displayCapacity =
    isMinVehicleSmall && minCap && totalUnits
      ? totalUnits * minCap
      : realCapacity;

  // Texto de combinación (modo no-cards)
  const combinationText = useMemo(() => {
    if (!selectedModels.length) return "";

    if (isMinVehicleSmall && minModel && minCap && totalUnits) {
      const txt = `${totalUnits} × ${minModel.label}`;
      return `${txt} (${minCap})`;
    }

    return selectedModels
      .map(({ units, model }) => {
        const base = `${units} × ${model.label}`;
        return Number.isFinite(model.maxPeople)
          ? `${base} (${model.maxPeople})`
          : base;
      })
      .join(" + ");
  }, [
    selectedModels,
    isMinVehicleSmall,
    minModel,
    minCap,
    totalUnits,
  ]);

  // Para modo cards normal (sin enmascarar)
  const unitsByValue = useMemo(() => {
    const map = {};
    (formData?.tuktukModels || []).forEach((sel) => {
      map[sel.value] = sel.units;
    });
    return map;
  }, [formData?.tuktukModels]);

  const { t } = useTranslation(textOrigin);
  const tt = (field) => t(`${textBranch}.${field}`);

  //=========== Render ===========//

  const tukCards = (tuktukModels || [])
    .filter((m) => m.label?.toLowerCase().startsWith("tuktuk"))
    .sort((a, b) => (a.maxPeople || 0) - (b.maxPeople || 0));

  return (
    <PFieldsetWrapper label={tt("modelField2")}>
      <CText>{`${tt("passengersField")}: ${persons}`}</CText>

      {!isTuk && (
        <CText>{`${tt("capcityField")}: ${displayCapacity} plazas`}</CText>
      )}

      {isTuk ? (
        <div
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
            marginTop: spacingFluidSmall,
          }}
        >
          {isMinVehicleSmall ? (
            // En modo small:
            // - Si hay selección válida => SOLO card del modelo pequeño.
            // - Si no hay selección => no mostramos nada (solo WhatsApp abajo).
            minModel && totalUnits > 0 ? (
              <TuktukCard
                key={minModel.value || "min-model"}
                label={minModel.label}
                capacity={minCap}
                units={totalUnits}
              />
            ) : null
          ) : (
            // Modo normal: mostramos todas las cards con sus unidades.
            tukCards.map((model) => (
              <TuktukCard
                key={model.value}
                label={model.label}
                capacity={model.maxPeople}
                units={unitsByValue[model.value] || 0}
              />
            ))
          )}
        </div>
      ) : (
        combinationText && (
          <CText>{`${tt("combinationField")}: ${combinationText}`}</CText>
        )
      )}

      {/* Si lo que ve el cliente no cubre a todos, WhatsApp */}
      {displayCapacity < persons && (
        <div style={{ marginTop: spacingFluidSmall }}>
          <CButton
            icon="whatsapp"
            onClick={() =>
              window.open(
                `https://wa.me/${userData?.data?.contact_number}?text=Hola,%20necesito%20información%20sobre%20la%20reserva.`,
                "_blank"
              )
            }
            style={{ fontSize: "0.9rem" }}
          >
            {tt("whatsappButton")}
          </CButton>
        </div>
      )}
    </PFieldsetWrapper>
  );
};

export default AutoTuktukModelsSection;

/*************************  LÓGICA DE CÁLCULO *************************/

/**
 * Modo normal: minimiza plazas sobrantes y nº de vehículos.
 */
function computeBestCombination(models, persons) {
  if (!Array.isArray(models) || models.length === 0) return null;

  const unitsList = [];
  models.forEach((m, idx) => {
    const available = Number.isFinite(m.availableUnits) ? m.availableUnits : 0;
    const cap = Number.isFinite(m.maxPeople) ? m.maxPeople : 0;
    for (let i = 0; i < available; i++) {
      unitsList.push({ idx, capacity: cap });
    }
  });

  const totalSeats = unitsList.reduce((sum, u) => sum + u.capacity, 0);
  if (totalSeats < persons) return null;

  const dp = Array(totalSeats + 1).fill(null);
  dp[0] = [];

  unitsList.forEach((unit) => {
    for (let seats = totalSeats - unit.capacity; seats >= 0; seats--) {
      if (dp[seats] !== null) {
        const next = seats + unit.capacity;
        const candidate = [...dp[seats], unit.idx];
        if (dp[next] === null || candidate.length < dp[next].length) {
          dp[next] = candidate;
        }
      }
    }
  });

  let best = null;
  for (let seats = persons; seats <= totalSeats; seats++) {
    if (dp[seats] !== null) {
      const waste = seats - persons;
      const count = dp[seats].length;
      if (
        !best ||
        waste < best.waste ||
        (waste === best.waste && count < best.count)
      ) {
        best = { seats, waste, count };
      }
    }
  }

  if (!best || !dp[best.seats]) return null;

  const counts = {};
  dp[best.seats].forEach((idx) => {
    counts[idx] = (counts[idx] || 0) + 1;
  });

  return Object.entries(counts).map(([idx, units]) => ({
    model: models[idx],
    units,
  }));
}

/**
 * Modo isMinVehicleSmall:
 * - Cliente solo ve el modelo pequeño.
 * - Todos los vehículos cuentan como si fueran del pequeño.
 * - Internamente elegimos EXACTAMENTE neededVehicles vehículos reales
 *   (respetando availableUnits).
 * - Si no hay suficientes vehículos totales => null (se fuerza WhatsApp).
 */
function computeMinVehicleSmallSelection(models, persons) {
  if (
    !Array.isArray(models) ||
    models.length === 0 ||
    !persons ||
    persons <= 0
  ) {
    return null;
  }

  const valid = models
    .map((m) => ({
      ...m,
      maxPeople: Number.isFinite(m.maxPeople) ? m.maxPeople : 0,
      availableUnits: Number.isFinite(m.availableUnits)
        ? m.availableUnits
        : 0,
    }))
    .filter((m) => m.maxPeople > 0 && m.availableUnits > 0);

  if (!valid.length) return null;

  const minModel = valid.reduce((min, m) =>
    m.maxPeople < min.maxPeople ? m : min
  );
  const minCap = minModel.maxPeople;

  const neededVehicles = Math.ceil(persons / minCap);

  const totalAvailable = valid.reduce(
    (acc, m) => acc + m.availableUnits,
    0
  );

  if (neededVehicles > totalAvailable) {
    return null;
  }

  // Asignamos EXACTAMENTE neededVehicles vehículos reales.
  // Priorizamos los más pequeños (esto mantiene coherencia visual/coste).
  const sorted = [...valid].sort((a, b) => a.maxPeople - b.maxPeople);

  const result = [];
  let remaining = neededVehicles;

  for (const model of sorted) {
    if (remaining <= 0) break;
    const take = Math.min(model.availableUnits, remaining);
    if (take > 0) {
      result.push({ model, units: take });
      remaining -= take;
    }
  }

  if (remaining > 0) {
    return null;
  }

  return result;
}
